import type { NeoInsightLatest } from 'src/hooks/useNeoInsights'

/**
 * Republic Services morning insight — the framing shown on the Insights
 * screen when the Republic Services demo set is active. Three recommendations
 * mirror the three acts of the live demo: Atlanta Baler #2, the Machinex
 * Hyspec lot warranty campaign, and the Gulf Coast humidity ramp.
 */
export const republicServicesInsight: NeoInsightLatest = {
  id: 'demo-insight-republic-services-2026-05-15',
  type: 'DAILY',
  status: 'ACTIVE',
  userEmail: null,
  summaryText:
    'Across your 47 MRFs this morning, three issues need attention. **Atlanta** is tracking a 132-ton miss against its 462-ton plan — every minute of unplanned downtime traces to Primary Baler #2, which Neo predicts will need a hydraulic seal kit replacement within 7–10 days. A **fleet-wide warranty pattern** on Machinex MACH Hyspec sorters from lot MX-2022-Q4-B is affecting 4 facilities (Austin, Scottsdale, Phoenix, Sacramento), with ~$250K of avoidable downtime risk and a Machinex warranty conversation on the table. And the **Gulf Coast humidity ramp** is starting — Houston, New Orleans, and Tampa OCC screens are showing the leading-edge signature for the June–August jam season.',
  generatedAt: new Date().toISOString(),
  startDate: null,
  endDate: null,
  insights: {
    recommendations: [
      {
        id: 'rs-rec-baler2-atlanta',
        title:
          'Atlanta Primary Baler #2 — preventive hydraulic seal service Thursday May 21, 2–4 AM',
        description:
          'Neo predicts 78% probability of hydraulic seal failure on Primary Baler #2 (Bollegraaf HBC-110, SN BG2018-00042) between May 22–25. Today: route 25–30% of OCC to Secondary Baler and raise the cycle target to 41s to shrink the current 132-ton miss. This week: scheduled preventive service Thursday 2–4 AM with parts in stock and Martinez J. available. ~$1,200 of preventive cost avoids ~$50K of failure cost, with 7–10 days of lead time.',
        priority: 'high',
        category: 'reliability',
        dataPoints: [
          'SCADA: cycle time +6.5% over 10 days, hydraulic pressure variance +47% over 5 days',
          'MPower: 47,000 runtime hours, last seal replacement 14 months ago (Bollegraaf HBC-110 guidance: 12–18 months)',
          'OIS: 2 slow-cycle operator notes in the last 7 days + a wire feed misfeed yesterday',
          'Cross-network: 3 prior HBC-110 hydraulic seal failures (IND, FW, DAL) — 91% signature match',
        ],
        actionableSteps: [
          'Approve operational work order: route 25–30% OCC to Secondary Baler, cycle target 41s (today)',
          'Approve scheduled work order: hydraulic seal kit replacement Thu May 21 2–4 AM, Martinez J.',
          'Re-measure projected EOD and pace 2 hours after operational change applies',
        ],
        estimatedImpact:
          "Shrink today's 132-ton miss to ~50 tons; avoid ~$50K of failure cost; prevent unplanned downtime May 22–25.",
      },
      {
        id: 'rs-rec-hyspec-warranty-campaign',
        title:
          'Machinex MACH Hyspec MX-2022-Q4-B lot — fleet warranty campaign at 4 facilities',
        description:
          "Neo correlated equipment manufacturer lot data from MPower's warranty records with failure history across the network. Machinex MACH Hyspec optical sorters from manufacturer lot MX-2022-Q4-B (installed Sep 2022 – Apr 2023) are failing at an 83% rate on the ejection actuator solenoid. Scottsdale (Apr 5) and Phoenix (Apr 27) already failed; Austin and Sacramento show the same calibration drift signature now. Engage Machinex on warranty; schedule preventive solenoid replacement at the 2 unfailed sites.",
        priority: 'high',
        category: 'reliability',
        dataPoints: [
          '12 Hyspec units across 4 facilities from lot MX-2022-Q4-B',
          '10 failures since 2024 — 83% lot failure rate',
          'Scottsdale and Phoenix already failed; Austin and Sacramento predicted within 60 days',
          'Other Hyspec lots: <12% failure rate across 34 other units in the network',
        ],
        actionableSteps: [
          'Open warranty case with Machinex citing the lot pattern and failure mode',
          'Schedule preventive solenoid replacement at Austin and Sacramento before the predicted window',
          'Add lot MX-2022-Q4-B to the calibration-drift watch list across all 12 affected units',
        ],
        estimatedImpact:
          '~$250K of avoidable downtime across 4 facilities; potential full or partial warranty recovery on solenoid replacements.',
      },
      {
        id: 'rs-rec-gulf-humidity-prep',
        title:
          'Gulf Coast OCC screens — pre-position parts and cleaning shifts for June–August',
        description:
          'Houston, New Orleans, and Tampa OCC screens are showing the leading-edge signature for the June–August humidity jam season. Current vibration is up 15% week-over-week — matching the 2023, 2024, and 2025 summer-onset patterns. Pre-stock OCC bearings and disc sets at all 3 sites. Pre-schedule 2 extra cleaning shifts per week for June through August.',
        priority: 'medium',
        category: 'reliability',
        dataPoints: [
          '3 prior summers (2023–2025) show 2–3× baseline jam rate in Jun–Aug at Gulf facilities',
          'Current OCC screen vibration: +15% week-over-week at Houston, New Orleans, Tampa',
          'Lead time before predicted window: ~2 weeks',
        ],
        actionableSteps: [
          'Pre-position OCC screen bearings + disc sets at Houston, New Orleans, Tampa',
          'Pre-schedule 2 extra cleaning shifts per week, Jun–Aug, at the 3 sites',
          'Re-evaluate vibration signature weekly through end of May',
        ],
        estimatedImpact:
          'Move from reactive cleaning to anticipatory; estimated 30–40% reduction in summer jam-driven downtime across the 3 sites.',
      },
    ],
  },
}
